import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, FormControl, Select, MenuItem } from '@material-ui/core';
import { css } from '@emotion/react';
import { MODALS, ROUTE_COLORS } from '../constants';
import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined';
import { PlanningDataContext, PlanningSelectionContext, PlanningUiContext } from '../context';
import { useClientUser } from '@/hooks/useClientUser';
import { colors } from '@/styles';
import { Body1, GridItemRow } from '../blocks';
import SendToOnward from './SendToOnward';
import { LTL_MARKETPLACE } from '@/constants/featureFlags';
import { PrimaryButton } from '@/styles/blocks';
import { useAssignToRoute } from './useAssignToRoute';
import { routeDisplayName } from '@/utilities/routeNames';

const AssignToRoute = () => {
    const { selectedRoute, selectedOrders } = useContext(PlanningSelectionContext);
    const { setModalOpen } = useContext(PlanningUiContext);
    const {
        state: { routes, loading },
    } = useContext(PlanningDataContext);
    const [assignedRoute, setAssignedRoute] = useState(selectedRoute || '');
    const { circles } = useClientUser();
    const { assign, getOrdersToAdd, assignLoading } = useAssignToRoute();

    // Default the route selector to the currently selected route so clicking a route
    // on the map/route list pre-fills the assignment target. Re-syncs whenever the
    // selection changes; the user can still override the choice in the dropdown.
    useEffect(() => {
        setAssignedRoute(selectedRoute || '');
    }, [selectedRoute]);

    const ordersToAdd = useMemo(
        () => getOrdersToAdd(selectedOrders, assignedRoute),
        [getOrdersToAdd, selectedOrders, assignedRoute]
    );

    const routesByKey = useMemo(() => {
        return Object.fromEntries(routes.map((route) => [route.route_id, route]));
    }, [routes]);

    const assignedRouteObject = routesByKey[assignedRoute];

    const isLoading = loading || assignLoading;

    return (
        <Box
            css={css`
                background-color: white;
                width: 300px;
                position: fixed;
                right: 16px;
                bottom: 16px;
                border: 1px solid rgba(0, 0, 0, 0.32);
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
                border-radius: 5px 5px 0px 0px;
            `}
        >
            <GridItemRow
                css={css`
                    border-bottom: 1px solid #e2e2e2;
                    padding: 0.875rem 1rem 0.875rem 1rem;
                `}
            >
                <Body1
                    css={css`
                        color: ${colors.greys.primary};
                    `}
                >
                    Assign to
                </Body1>
                <Body1>{Object.entries(selectedOrders).filter(([, toggle]) => toggle)?.length} orders selected</Body1>
            </GridItemRow>
            <GridItemRow
                css={css`
                    padding: 0.875rem 1rem 0.375rem 1rem;
                    fieldset {
                        top: 0;
                    }
                `}
            >
                <FormControl fullWidth variant="outlined">
                    <Select
                        value={assignedRoute || 'NONE'}
                        onChange={(e) => setAssignedRoute(e.target.value)}
                        disabled={!routes.length}
                    >
                        <MenuItem value="NONE" key="REMOVE">
                            Select a Route
                        </MenuItem>
                        {routes.map((route, i) => (
                            <MenuItem value={route.route_id} key={`route-menu-item-${route.route_id}`}>
                                <LocalShippingOutlinedIcon
                                    fontSize="small"
                                    css={css`
                                        color: ${route.route_color || ROUTE_COLORS[i] || 'black'};
                                        margin-right: 5px;
                                    `}
                                />
                                {routeDisplayName(route)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </GridItemRow>
            <GridItemRow
                css={css`
                    padding: 0.375rem 1rem 0.875rem 1rem;
                `}
            >
                <PrimaryButton
                    fullWidth
                    disabled={isLoading || !assignedRouteObject}
                    onClick={() => assign(selectedOrders, assignedRoute)}
                >
                    {assignedRouteObject && assignedRouteObject.status !== 'planning'
                        ? 'Route is Locked'
                        : `Assign to Route`}
                </PrimaryButton>
            </GridItemRow>
            <GridItemRow
                css={css`
                    padding: 0.375rem 1rem 0.875rem 1rem;
                `}
            >
                <PrimaryButton fullWidth disabled={isLoading} onClick={() => setModalOpen(MODALS.ORDER_RESCHEDULE)}>
                    Reschedule Orders
                </PrimaryButton>
            </GridItemRow>
            {circles?.[LTL_MARKETPLACE] ? <SendToOnward orders={ordersToAdd} /> : null}
        </Box>
    );
};

export default AssignToRoute;
